import React, { useState, useEffect, useRef, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { getDashboardData } from 'services/dashboard/dashboardService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from "react-redux";
import { DashboardData } from 'types/dashboard';
import InstagramReloginModal from "components/modals/InstagramReloginModal";
import AddAccountOverlay from "components/modals/AddAccountOverlay";
import Spinner from "components/common/Spinner";
import { AppDispatch } from "config/redux/store";
import { setAccountBlocked, setSelectedUser } from "config/redux/slices/userSlice";
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import {
  LayoutDashboard, Zap, Heart, MessageCircleMore, UserRoundPlus, Send,
  StickyNote, SquarePlay, ChevronsUp, ChevronsDown, ChevronDown, CalendarDays,
  Info, X, Bell, CheckCircle2, AlertTriangle, Moon, Lock, Menu,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ZoomIn, ZoomOut, ScanSearch, RotateCcw, EllipsisVertical,
} from 'lucide-react';
import ApexCharts from 'apexcharts';

// Define interfaces for data structures
interface Account {
  _id: string;
  imgSrc: string;
  name: string;
  username: string;
  is_locked?: boolean;
  lock_title?: { en: string; fr: string };
  lock_description?: { en: string; fr: string };
  locked_by_admin?: boolean;
  is_active?: boolean;
  connection_status?: string;
  freeze_until?: string;
  next_job_time?: string;
  last_job_run?: string;
  all_jobs_completed?: boolean;
  is_sleeping?: boolean;
  account_status?: string;
}

interface StatBadge {
  value: number;
  percentChange: number;
  direction: 'up' | 'down' | 'none';
  showFire: boolean;
}

interface Stat {
  label: string;
  count: number;
  route: string;
  suffix?: string;
  badge?: StatBadge;
  hidden?: boolean;
  disabled?: boolean;
}

interface ChartSeries {
  name: string;
  data: number[];
}

interface ChartData {
  series: ChartSeries[];
  categories: string[];
  visibleSeriesIndices?: number[];
}

interface InsightsData {
  categories: string[];
  data: number[];
}

interface AnalysisData {
  categories: string[];
  data: number[];
}

interface InsightsCharts {
  followerData: InsightsData;
  followingData: InsightsData;
  mediaData: InsightsData;
  analysisData: AnalysisData;
}

interface UserState {
  user: {
    selectedUser: string | null;
    daysRemaining: number;
    isAccountBlocked: boolean;
  };
}

// Badge color palettes based on trend
const BADGE_COLORS = {
  fire: { dark: { bg: '#461901', border: '#E17100', color: '#FFD230', chevron: '#FFBA00' }, light: { bg: '#FFFBEB', border: '#FFD230', color: '#E17100', chevron: '#E17100' } },
  up: { dark: { bg: '#022C22', border: '#009966', color: '#5EE9B5', chevron: '#00D492' }, light: { bg: '#ECFDF5', border: '#5EE9B5', color: '#009966', chevron: '#009966' } },
  none: { dark: { bg: '#0F172B', border: '#314158', color: '#90A1B9', chevron: '#90A1B9' }, light: { bg: '#FFFFFF', border: '#E2E8F0', color: '#62748E', chevron: '#62748E' } },
};

// Overview card visual config
const OVERVIEW_CARD_CONFIG = [
  {
    key: 'rules',
    icon: Zap,
    iconColor: { dark: '#51A2FF', light: '#2B7FFF' },
    gradientAccent: { dark: '#162456', light: '#EFF6FF' },
    borderColor: { dark: '#1E3A5F', light: '#DBEAFE' },
  },
  {
    key: 'likes',
    icon: Heart,
    iconColor: { dark: '#FF637E', light: '#FF2056' },
    gradientAccent: { dark: '#4D0218', light: '#FFF1F2' },
    borderColor: { dark: '#5C1A2A', light: '#FECDD3' },
  },
  {
    key: 'comments',
    icon: MessageCircleMore,
    iconColor: { dark: '#00D3F2', light: '#00B8DB' },
    gradientAccent: { dark: '#053345', light: '#ECFEFF' },
    borderColor: { dark: '#0E4D5E', light: '#CFFAFE' },
  },
  {
    key: 'followings',
    icon: UserRoundPlus,
    iconColor: { dark: '#00D492', light: '#00BC7D' },
    gradientAccent: { dark: '#022C22', light: '#ECFDF5' },
    borderColor: { dark: '#0D3D30', light: '#A7F3D0' },
  },
  {
    key: 'messages',
    icon: Send,
    iconColor: { dark: '#C27AFF', light: '#AD46FF' },
    gradientAccent: { dark: '#3C0366', light: '#FAF5FF' },
    borderColor: { dark: '#4C1D7A', light: '#E9D5FF' },
  },
  {
    key: 'posts',
    icon: StickyNote,
    iconColor: { dark: '#FFBA00', light: '#FD9A00' },
    gradientAccent: { dark: '#461901', light: '#FFFBEB' },
    borderColor: { dark: '#5C2E0E', light: '#FDE68A' },
  },
  {
    key: 'storyViewed',
    icon: SquarePlay,
    iconColor: { dark: '#FB64B6', light: '#F6339A' },
    gradientAccent: { dark: '#510424', light: '#FDF2F8' },
    borderColor: { dark: '#6B1A3D', light: '#FBCFE8' },
  },
];

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();
  const [activeButton, setActiveButton] = useState<string>(t('dashboard.dateFilters.last30Days'));
  const [achievedValue, setAchievedValue] = useState<number>(0);
  const [rulesCount, setRulesCount] = useState<number>(0);
  const { selectedUser, daysRemaining, isAccountBlocked } = useSelector((state: UserState) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReauthOverlayOpen, setIsReauthOverlayOpen] = useState(false);
  const [reauthUsername, setReauthUsername] = useState<string | undefined>(undefined);
  const [reauthProfilePic, setReauthProfilePic] = useState<string | undefined>(undefined);
  const [progressPercentage, setProgressValue] = useState<number>(0);
  const [chartData, setChartData] = useState<ChartData>({
    series: [
      { name: t('dashboard.stats.likes'), data: [] },
      { name: t('dashboard.stats.messages'), data: [] },
      { name: t('dashboard.stats.comments'), data: [] },
      { name: t('dashboard.stats.followings'), data: [] },
      { name: t('dashboard.stats.posts'), data: [] },
      { name: t('dashboard.stats.storyView'), data: [] },
    ],
    categories: [],
  });
  const [insightsCharts, setInsightsCharts] = useState<InsightsCharts>({
    followerData: { categories: [], data: [] },
    followingData: { categories: [], data: [] },
    mediaData: { categories: [], data: [] },
    analysisData: { categories: [], data: [] },
  });
  const [stats, setStats] = useState<Stat[]>([
    { label: t('dashboard.stats.activeRules', 'Active Rules'), count: 0, route: '/rules' },
    { label: t('dashboard.stats.likes'), count: 0, route: '/actions/likes' },
    { label: t('dashboard.stats.comments'), count: 0, route: '/actions/comments' },
    { label: t('dashboard.stats.followings'), count: 0, route: '/actions/followings' },
    { label: t('dashboard.stats.messages'), count: 0, route: '/actions/messages' },
    { label: t('dashboard.stats.posts'), count: 0, route: '/actions/posts' },
    { label: t('dashboard.stats.storyView'), count: 0, route: '/actions/story' },
  ]);
  const [targetValue, setTargetValue] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [showNextJobNotification, setShowNextJobNotification] = useState<boolean>(false);
  const [hoursRemaining, setHoursRemaining] = useState<number | null>(null);
  const [nextJobHoursRemaining, setNextJobHoursRemaining] = useState<number | null>(null);
  const [isMinutesFreeze, setIsMinutesFreeze] = useState<boolean>(false);
  const [isMinutesNextJob, setIsMinutesNextJob] = useState<boolean>(false);
  const [showSleepingNotification, setShowSleepingNotification] = useState<boolean>(false);
  const [showAllJobsCompletedNotification, setShowAllJobsCompletedNotification] = useState<boolean>(true);
  const [analysisBadge, setAnalysisBadge] = useState<StatBadge | null>(null);
  const [instagramViews, setInstagramViews] = useState<number | null>(null);

  // State for custom date range calendar
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [calendarMonth, setCalendarMonth] = useState<number>(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState<number>(new Date().getFullYear());
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [rangeEnd, setRangeEnd] = useState<string | null>(null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // State for user dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown / date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inDesktop = dropdownRef.current?.contains(target);
      const inMobile = mobileDropdownRef.current?.contains(target);
      if (!inDesktop && !inMobile) {
        setIsDropdownOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleAccountSelect = (account: Account) => {
    setSelectedAccount(account);
    setIsDropdownOpen(false);
    dispatch(setSelectedUser(account._id));
    refreshData(account._id);
  };

  const handleRelogin = () => {
    setReauthUsername(selectedAccount?.username);
    setReauthProfilePic(selectedAccount?.imgSrc);
    setIsReauthOverlayOpen(true);
    setIsModalOpen(false);
  };

  const handleLater = () => {
    dispatch(setAccountBlocked(true));
    setIsModalOpen(false);
  };


  const handleCloseNotification = () => setShowNotification(false);
  const handleCloseNextJobNotification = () => setShowNextJobNotification(false);

  const calculateTimeRemaining = (dateTime: string): { value: number; isMinutes: boolean } => {
    const targetTime = new Date(dateTime).getTime();
    const now = new Date().getTime();
    const diffMs = targetTime - now;
    const minutes = Math.max(0, Math.round(diffMs / (1000 * 60)));
    if (minutes < 60) return { value: minutes, isMinutes: true };
    return { value: Math.round(minutes / 60), isMinutes: false };
  };

  const filterInsightsByDateRange = (insights: DashboardData['insights'], dateFilter: string): NonNullable<DashboardData['insights']> => {
    if (!insights || insights.length === 0) return [];
    const now = new Date();
    let cutoffDate = new Date(0);
    switch (dateFilter) {
      case 'Today': cutoffDate = new Date(now.setHours(0, 0, 0, 0)); break;
      case 'Last 7 days': cutoffDate = new Date(now.setDate(now.getDate() - 7)); break;
      case 'Last 30 days': cutoffDate = new Date(now.setDate(now.getDate() - 30)); break;
      default: return insights;
    }
    return insights.filter((insight) => new Date(insight.created_at) >= cutoffDate);
  };

  const processViewsData = (dashboardData: DashboardData['data']): AnalysisData => {
    if (!dashboardData || dashboardData.length <= 1) return { categories: [], data: [] };
    const entries = dashboardData.slice(1) as Array<{ Date: string; Likes?: number; Messages?: number; Comments?: number; Followings?: number; Posts?: number; StoryViews?: number }>;
    const sorted = [...entries].sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
    return {
      categories: sorted.map((e) => new Date(e.Date).toLocaleDateString(i18n.language || 'en', { month: 'short', day: 'numeric' })),
      data: sorted.map((e) => (e.Likes || 0) + (e.Comments || 0) + (e.Followings || 0) + (e.Messages || 0) + (e.Posts || 0) + (e.StoryViews || 0)),
    };
  };

  const formatLastRunTime = (utcDateString?: string): string => {
    if (!utcDateString) return '';
    const now = new Date();
    const lastRun = new Date(utcDateString);
    const diffMinutes = Math.floor((now.getTime() - lastRun.getTime()) / (1000 * 60));
    if (now.toDateString() === lastRun.toDateString()) {
      if (diffMinutes < 60) return t('dashboard.time.minutesAgo', { count: diffMinutes });
      const hours = Math.floor(diffMinutes / 60);
      return hours === 1 ? t('dashboard.time.hourAgo', { count: hours }) : t('dashboard.time.hoursAgo', { count: hours });
    }
    return lastRun.toLocaleDateString(i18n.language || 'en', { day: '2-digit', month: 'short' });
  };

  const processInsightsData = (insights: DashboardData['insights'], dashboardData: DashboardData['data'], dateFilter: string): InsightsCharts => {
    const analysisData = processViewsData(dashboardData);
    if (!insights || insights.length === 0) {
      return {
        followerData: { categories: [], data: [] },
        followingData: { categories: [], data: [] },
        mediaData: { categories: [], data: [] },
        analysisData,
      };
    }
    const filteredInsights = filterInsightsByDateRange(insights, dateFilter);
    const latestInsightsMap = new Map<string, NonNullable<DashboardData['insights']>[number]>();
    const getDateKey = (date: string): string => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    filteredInsights.forEach((insight) => {
      const dateKey = getDateKey(insight.created_at);
      if (!latestInsightsMap.has(dateKey)) {
        latestInsightsMap.set(dateKey, insight);
      } else {
        const existing = latestInsightsMap.get(dateKey);
        if (existing && new Date(insight.created_at).getTime() > new Date(existing.created_at).getTime()) {
          latestInsightsMap.set(dateKey, insight);
        }
      }
    });
    const sortedEntries = Array.from(latestInsightsMap.entries()).sort((a, b) => new Date(a[1].created_at).getTime() - new Date(b[1].created_at).getTime());
    const categories = sortedEntries.map(([dateKey]) => new Date(dateKey).toLocaleDateString(i18n.language || 'en', { month: 'short', day: 'numeric' }));
    return {
      followerData: { categories, data: sortedEntries.map(([, i]) => i.follower_count || 0) },
      followingData: { categories, data: sortedEntries.map(([, i]) => i.following_count || 0) },
      mediaData: { categories, data: sortedEntries.map(([, i]) => i.media_count || 0) },
      analysisData,
    };
  };

  // Compute badge for a stat by comparing current vs previous value
  const computeBadge = (current: number, previous: number): StatBadge => {
    if (previous === 0 && current === 0) return { value: 0, percentChange: 0, direction: 'none', showFire: false };
    if (previous === 0) return { value: current, percentChange: 100, direction: 'up', showFire: current > 0 };
    const diff = current - previous;
    const percentChange = Math.round((diff / previous) * 100);
    const direction: 'up' | 'down' | 'none' = diff > 0 ? 'up' : diff < 0 ? 'down' : 'none';
    const showFire = percentChange >= 70;
    return { value: diff, percentChange: Math.abs(percentChange), direction, showFire };
  };

  const refreshData = async (accountId: string | null) => {
    if (!accountId) {
      setError(t('dashboard.errors.noAccountSelected'));
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      let dateFilter: string;
      switch (activeButton) {
        case t('dashboard.dateFilters.todays'): dateFilter = 'Today'; break;
        case t('dashboard.dateFilters.last7Days'): dateFilter = 'Last 7 days'; break;
        case t('dashboard.dateFilters.last30Days'): dateFilter = 'Last 30 days'; break;
        case t('dashboard.dateFilters.custom'): dateFilter = 'Custom'; break;
        default: dateFilter = 'Last 30 days';
      }
      await fetchData(dateFilter, accountId, dateFilter === 'Custom' ? { start_date: customStartDate, end_date: customEndDate } : undefined);
    } catch (err: any) {
      const errorMessage = err.message || t('dashboard.errors.failedToRefresh');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async (dateFilter: string, accountId: string | null = null, options?: { start_date?: string; end_date?: string }) => {
    try {
      if (!accountId) throw new Error(t('dashboard.errors.noAccountSelectedShort'));
      const response: DashboardData = await getDashboardData(dateFilter, accountId, options);
      if (!response) throw new Error(t('dashboard.errors.noResponse'));

      const { data, total_action_limit, total_limit_utilized, accounts: apiAccounts, insights, rules_count, actions, variations } = response;

      const transformedAccounts = Array.isArray(apiAccounts) ? apiAccounts : apiAccounts ? [apiAccounts] : [];
      const accountsData = transformedAccounts
        .map((account) => {
          if (!account || !account._id) return null;
          try {
            return {
              _id: account._id,
              imgSrc: account.profile_picture_base64 || 'data:image/jpeg;base64,/9j/4AAQSkZJREQf/2Q==',
              name: account.name || account.username || 'Unknown Account',
              username: account.username || 'unknown',
              is_locked: account.is_locked || false,
              lock_title: account.lock_title,
              lock_description: account.lock_description,
              locked_by_admin: account.locked_by_admin || false,
              is_active: account.is_active !== undefined ? account.is_active : true,
              connection_status: account.connection_status || '',
              freeze_until: account.freeze_until,
              next_job_time: account.next_job_time,
              last_job_run: account.last_job_run,
              all_jobs_completed: account.all_jobs_completed || false,
              is_sleeping: account.is_sleeping || false,
              account_status: account.account_status || 'Active',
            } as Account;
          } catch { return null; }
        })
        .filter((account): account is Account => account !== null);

      setAccounts(accountsData);

      if (accountsData.length > 0) {
        const selected = accountsData.find(acc => acc._id === accountId) || accountsData[0];

        // Sleeping hours notification
        if (selected.is_sleeping || selected.account_status === 'Sleeping hours') {
          setShowSleepingNotification(true);
        } else {
          setShowSleepingNotification(false);
        }

        // All jobs completed notification (only when NOT sleeping)
        if (selected.all_jobs_completed && selected.account_status === 'All jobs completed' && !selected.is_sleeping) {
          setShowAllJobsCompletedNotification(true);
        }

        let freezeTime = { value: 0, isMinutes: false };
        if (selected.freeze_until) {
          freezeTime = calculateTimeRemaining(selected.freeze_until);
          if (freezeTime.value > 0) {
            setHoursRemaining(freezeTime.value);
            setIsMinutesFreeze(freezeTime.isMinutes);
            setShowNotification(true);
          } else {
            setShowNotification(false); setHoursRemaining(null); setIsMinutesFreeze(false);
          }
        } else {
          setShowNotification(false); setHoursRemaining(null); setIsMinutesFreeze(false);
        }

        if (selected.next_job_time && !freezeTime.value) {
          const nextJobTime = calculateTimeRemaining(selected.next_job_time);
          if (nextJobTime.value > 0) {
            setNextJobHoursRemaining(nextJobTime.value);
            setIsMinutesNextJob(nextJobTime.isMinutes);
            setShowNextJobNotification(true);
          } else {
            setShowNextJobNotification(false); setNextJobHoursRemaining(null); setIsMinutesNextJob(false);
          }
        } else {
          setShowNextJobNotification(false); setNextJobHoursRemaining(null); setIsMinutesNextJob(false);
        }

        if (!selectedAccount || !accountsData.some((acc) => acc._id === selectedAccount._id)) {
          setSelectedAccount(selected);
          if (selected.is_locked && !isAccountBlocked) setIsModalOpen(true);
        }
      } else {
        setSelectedAccount(null); setShowNotification(false); setShowNextJobNotification(false);
      }

      // Determine which actions are disabled (settings_value === false or action_details.is_active === false)
      const isActionEnabled = (action: NonNullable<DashboardData['actions']>[number]) => {
        if (action.action_details?.is_active === false) return false;
        if (action.action_settings?.settings_value === false) return false;
        return true;
      };

      // Compute daily action usage totals excluding disabled actions
      let disabledLimit = 0;
      let disabledUtilized = 0;
      // Actions whose limit should be excluded but utilized count kept (e.g. story likes)
      const EXCLUDE_LIMIT_ACTIONS = ['likes_auto_like_stories'];
      let excludedLimitOnly = 0;
      // Compute per-category limits and track fully-disabled categories (only from enabled actions)
      const categoryLimits: Record<string, number> = {};
      const categoryHasEnabled: Record<string, boolean> = {};
      const categoryExists: Record<string, boolean> = {};
      if (actions && Array.isArray(actions)) {
        actions.forEach((action) => {
          const cat = action.action_details?.category;
          if (!cat) return;
          categoryExists[cat] = true;
          const enabled = isActionEnabled(action);
          const actionName = action.action_details?.name || '';
          const excludeLimit = EXCLUDE_LIMIT_ACTIONS.includes(actionName);
          if (enabled) {
            if (excludeLimit) {
              // Keep utilized in totals but exclude limit from category and daily total
              excludedLimitOnly += action.action_limit || 0;
            } else {
              categoryLimits[cat] = (categoryLimits[cat] || 0) + (action.action_limit || 0);
            }
            categoryHasEnabled[cat] = true;
          } else {
            disabledLimit += action.action_limit || 0;
            disabledUtilized += action.limit_utilized || 0;
          }
        });
      }
      const postsExist = !!categoryExists['Posts'];

      const adjustedLimit = (total_action_limit || 0) - disabledLimit - excludedLimitOnly;
      const adjustedUtilized = (total_limit_utilized || 0) - disabledUtilized;
      setAchievedValue(adjustedLimit);
      setRulesCount(rules_count || 0);
      setTargetValue(adjustedUtilized);
      setProgressValue(adjustedLimit && adjustedUtilized ? (adjustedUtilized / adjustedLimit) * 100 : 0);

      const isToday = dateFilter === 'Today';
      const likesLimit = isToday ? (categoryLimits['Likes'] || 0) : 0;
      const commentsLimit = isToday ? (categoryLimits['Comments'] || 0) : 0;
      const followingsLimit = isToday ? ((categoryLimits['Following'] || 0) + (categoryLimits['Followings'] || 0)) : 0;
      const messagesLimit = isToday ? (categoryLimits['Messages'] || 0) : 0;
      const postsLimit = isToday ? (categoryLimits['Posts'] || 0) : 0;

      // Hide flags will be computed after totals are known (need data counts for non-Today filters)
      const allDisabledLikes = !categoryHasEnabled['Likes'];
      const allDisabledComments = !categoryHasEnabled['Comments'];
      const allDisabledFollowings = !categoryHasEnabled['Following'] && !categoryHasEnabled['Followings'];
      const allDisabledMessages = !categoryHasEnabled['Messages'];
      const allDisabledPosts = !categoryHasEnabled['Posts'];

      if (insights || data) {
        setInsightsCharts(processInsightsData(insights, data, dateFilter));
      }

      // Extract latest instagram_views from insights (latest entry per date, pick most recent with value)
      if (insights && insights.length > 0) {
        // Sort by created_at descending to find the latest entry with instagram_views
        const sorted = [...insights].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const latestWithViews = sorted.find(i => i.instagram_views !== undefined);
        setInstagramViews(latestWithViews?.instagram_views ?? null);
      } else {
        setInstagramViews(null);
      }

      // Compute views badge from variations (total of all action variations)
      if (variations) {
        const allKeys = ['Likes', 'Comments', 'Followings', 'Messages', 'Posts', 'StoryViews'];
        const totalCurrent = allKeys.reduce((s, k) => s + (variations[k]?.current || 0), 0);
        const totalPrevious = allKeys.reduce((s, k) => s + (variations[k]?.previous || 0), 0);
        setAnalysisBadge(computeBadge(totalCurrent, totalPrevious));
      } else {
        setAnalysisBadge(null);
      }

      if (data && data.length > 1) {
        const categories: string[] = [];
        const likesData: number[] = [];
        const messagesData: number[] = [];
        const commentsData: number[] = [];
        const followingsData: number[] = [];
        const postsData: number[] = [];
        const storyData: number[] = [];

        data.slice(1).forEach((item) => {
          categories.push(new Date(item.Date).toLocaleDateString(i18n.language || 'en', { month: 'short', day: 'numeric' }));
          likesData.push(item.Likes || 0);
          messagesData.push(item.Messages || 0);
          commentsData.push(item.Comments || 0);
          followingsData.push(item.Followings || 0);
          postsData.push(item.Posts || 0);
          storyData.push(item.StoryViews || 0);
        });

        // Build all series, then filter out disabled categories with zero data
        const allSeries = [
          { name: t('dashboard.stats.likes'), data: likesData, disabled: allDisabledLikes },
          { name: t('dashboard.stats.messages'), data: messagesData, disabled: allDisabledMessages },
          { name: t('dashboard.stats.comments'), data: commentsData, disabled: allDisabledComments },
          { name: t('dashboard.stats.followings'), data: followingsData, disabled: allDisabledFollowings },
          { name: t('dashboard.stats.posts'), data: postsData, disabled: allDisabledPosts },
          { name: t('dashboard.stats.storyViews'), data: storyData, disabled: false },
        ];
        const visibleSeriesIndices = allSeries
          .map((s, i) => ({ ...s, idx: i }))
          .filter(s => !s.disabled || s.data.some(v => v > 0))
          .map(s => s.idx);

        setChartData({
          series: visibleSeriesIndices.map(i => ({ name: allSeries[i].name, data: allSeries[i].data })),
          categories,
          visibleSeriesIndices,
        });

        const totalLikes = likesData.reduce((s, v) => s + v, 0);
        const totalComments = commentsData.reduce((s, v) => s + v, 0);
        const totalFollowings = followingsData.reduce((s, v) => s + v, 0);
        const totalMessages = messagesData.reduce((s, v) => s + v, 0);
        const totalPosts = postsData.reduce((s, v) => s + v, 0);
        const totalStory = storyData.reduce((s, v) => s + v, 0);

        // Disable card if all actions in the category are disabled
        // Set stats immediately without badges, then fetch badges async
        setStats((prev) => [
          { ...prev[0], count: rules_count || 0, suffix: undefined, hidden: false, disabled: false },
          { ...prev[1], count: totalLikes, suffix: likesLimit ? `/${likesLimit}` : undefined, hidden: false, disabled: allDisabledLikes },
          { ...prev[2], count: totalComments, suffix: commentsLimit ? `/${commentsLimit}` : undefined, hidden: false, disabled: allDisabledComments },
          { ...prev[3], count: totalFollowings, suffix: followingsLimit ? `/${followingsLimit}` : undefined, hidden: false, disabled: allDisabledFollowings },
          { ...prev[4], count: totalMessages, suffix: messagesLimit ? `/${messagesLimit}` : undefined, hidden: false, disabled: allDisabledMessages },
          { ...prev[5], count: totalPosts, suffix: postsLimit ? `/${postsLimit}` : undefined, hidden: !postsExist, disabled: allDisabledPosts },
          { ...prev[6], count: totalStory, hidden: false, disabled: false },
        ]);

        // Compute badges from API variations data
        if (variations) {
          const variationKeys = ['Likes', 'Comments', 'Followings', 'Messages', 'Posts', 'StoryViews'];
          const badges: StatBadge[] = variationKeys.map((key) => {
            const v = variations[key];
            if (!v) return { value: 0, percentChange: 0, direction: 'none' as const, showFire: false };
            const pct = v.change_percent;
            const direction: 'up' | 'down' | 'none' = pct > 0 ? 'up' : pct < 0 ? 'down' : 'none';
            return { value: v.change, percentChange: Math.abs(Math.round(pct)), direction, showFire: pct >= 70 };
          });
          setStats((prev) => [
            { ...prev[0] },
            { ...prev[1], badge: badges[0] },
            { ...prev[2], badge: badges[1] },
            { ...prev[3], badge: badges[2] },
            { ...prev[4], badge: badges[3] },
            { ...prev[5], badge: badges[4] },
            { ...prev[6], badge: badges[5] },
          ]);
        }
      } else {
        setChartData({
          series: [
            { name: t('dashboard.stats.likes'), data: [] },
            { name: t('dashboard.stats.messages'), data: [] },
            { name: t('dashboard.stats.comments'), data: [] },
            { name: t('dashboard.stats.followings'), data: [] },
            { name: t('dashboard.stats.posts'), data: [] },
            { name: t('dashboard.stats.storyViews'), data: [] },
          ].filter((_, i) => {
            const disabledFlags = [allDisabledLikes, allDisabledMessages, allDisabledComments, allDisabledFollowings, allDisabledPosts, false];
            return !disabledFlags[i];
          }),
          categories: [],
        });
        setStats((prev) => [
          { ...prev[0], count: 0, suffix: undefined, hidden: false },
          { ...prev[1], count: 0, suffix: likesLimit ? `/${likesLimit}` : undefined, hidden: allDisabledLikes },
          { ...prev[2], count: 0, suffix: commentsLimit ? `/${commentsLimit}` : undefined, hidden: allDisabledComments },
          { ...prev[3], count: 0, suffix: followingsLimit ? `/${followingsLimit}` : undefined, hidden: allDisabledFollowings },
          { ...prev[4], count: 0, suffix: messagesLimit ? `/${messagesLimit}` : undefined, hidden: allDisabledMessages },
          { ...prev[5], count: 0, suffix: postsLimit ? `/${postsLimit}` : undefined, hidden: allDisabledPosts },
          { ...prev[6], count: 0, hidden: false },
        ]);
      }
    } catch (error: any) {
      const errorMessage = error.message || t('dashboard.errors.failedToLoad');
      setError(errorMessage);
      toast.error(errorMessage);
      setChartData({
        series: [
          { name: t('dashboard.stats.likes'), data: [] },
          { name: t('dashboard.stats.messages'), data: [] },
          { name: t('dashboard.stats.comments'), data: [] },
          { name: t('dashboard.stats.followings'), data: [] },
          { name: t('dashboard.stats.posts'), data: [] },
          { name: t('dashboard.stats.storyViews'), data: [] },
        ],
        categories: [],
      });
      throw error;
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response: DashboardData = await getDashboardData('Last 30 days', null);
        if (!response) throw new Error('No response from server');
        const { accounts: apiAccounts } = response;
        const transformedAccounts = Array.isArray(apiAccounts) ? apiAccounts : apiAccounts ? [apiAccounts] : [];
        const accountsData = transformedAccounts
          .map((account) => {
            if (!account || !account._id) return null;
            try {
              return {
                _id: account._id,
                imgSrc: account.profile_picture_base64 || 'data:image/jpeg;base64,/9j/4AAQSkZJREQf/2Q==',
                name: account.name || account.username || 'Unknown Account',
                username: account.username || 'unknown',
                is_locked: account.is_locked || false,
                lock_title: account.lock_title,
                lock_description: account.lock_description,
                locked_by_admin: account.locked_by_admin || false,
                connection_status: account.connection_status || '',
                freeze_until: account.freeze_until,
                next_job_time: account.next_job_time,
                last_job_run: account.last_job_run,
                all_jobs_completed: account.all_jobs_completed || false,
                is_sleeping: account.is_sleeping || false,
                account_status: account.account_status || 'Active',
              } as Account;
            } catch { return null; }
          })
          .filter((account): account is Account => account !== null);

        setAccounts(accountsData);

        if (accountsData.length > 0) {
          const selected = (selectedUser && accountsData.find(acc => acc._id === selectedUser)) || accountsData[0];

          // Sleeping hours notification
          if (selected.is_sleeping || selected.account_status === 'Sleeping hours') {
            setShowSleepingNotification(true);
          }

          // All jobs completed notification (only when NOT sleeping)
          if (selected.all_jobs_completed && selected.account_status === 'All jobs completed' && !selected.is_sleeping) {
            setShowAllJobsCompletedNotification(true);
          }

          let freezeTime = { value: 0, isMinutes: false };
          if (selected.freeze_until) {
            freezeTime = calculateTimeRemaining(selected.freeze_until);
            if (freezeTime.value > 0) {
              setHoursRemaining(freezeTime.value);
              setIsMinutesFreeze(freezeTime.isMinutes);
              setShowNotification(true);
            }
          }
          if (selected.next_job_time && !freezeTime.value) {
            const nextJobTime = calculateTimeRemaining(selected.next_job_time);
            if (nextJobTime.value > 0) {
              setNextJobHoursRemaining(nextJobTime.value);
              setIsMinutesNextJob(nextJobTime.isMinutes);
              setShowNextJobNotification(true);
            }
          }
          const showLockedModal = selected.is_locked && !isAccountBlocked;
          if (showLockedModal) setIsModalOpen(true);
          const connectionIssueAccount = accountsData.find(
            (acc) => acc.connection_status === 'connection_issue' || acc.connection_status === 'error' || acc.connection_status === 'failed'
          );
          if (connectionIssueAccount && !showLockedModal) {
            setReauthUsername(connectionIssueAccount.username);
            setReauthProfilePic(connectionIssueAccount.imgSrc || undefined);
            setIsReauthOverlayOpen(true);
          }
          setSelectedAccount(selected);
          dispatch(setSelectedUser(selected._id));
          await fetchData('Last 30 days', selected._id);
        } else {
          navigate('/accounts', { state: { autoOpenOverlay: true }, replace: true });
          return;
        }
      } catch (error: any) {
        const errorMessage = error.message || t('dashboard.errors.failedToLoad');
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedAccount) refreshData(selectedAccount._id);
  }, [activeButton]);

  // Auto-apply when both range dates are selected on the calendar
  useEffect(() => {
    if (rangeStart && rangeEnd && selectedAccount) {
      setCustomStartDate(rangeStart);
      setCustomEndDate(rangeEnd);
      setShowDatePicker(false);
      // Directly call fetchData with the range dates (avoids stale state issues)
      setActiveButton(t('dashboard.dateFilters.custom'));
      fetchData('Custom', selectedAccount._id, { start_date: rangeStart, end_date: rangeEnd });
    }
  }, [rangeEnd]);

  // Silent auto-refresh every 30 seconds (only when tab is visible)
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (!selectedAccount) return;
      let dateFilter: string;
      switch (activeButton) {
        case t('dashboard.dateFilters.todays'): dateFilter = 'Today'; break;
        case t('dashboard.dateFilters.last7Days'): dateFilter = 'Last 7 days'; break;
        case t('dashboard.dateFilters.last30Days'): dateFilter = 'Last 30 days'; break;
        case t('dashboard.dateFilters.custom'): dateFilter = 'Custom'; break;
        default: dateFilter = 'Last 30 days';
      }
      fetchData(dateFilter, selectedAccount._id, dateFilter === 'Custom' ? { start_date: customStartDate, end_date: customEndDate } : undefined).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedAccount, activeButton, customStartDate, customEndDate]);

  const handleButtonClick = (label: string) => setActiveButton(label);
  const handleStatClick = (route: string) => navigate(route);

  // ─── Calendar helpers ───
  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const getCalendarDays = (month: number, year: number): (number | null)[] => {
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const startOffset = firstDay === 0 ? 6 : firstDay - 1; // Mon-based offset
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (number | null)[] = [];
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const toDateStr = (day: number): string => {
    return `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleCalendarDayClick = (day: number) => {
    const dateStr = toDateStr(day);
    if (!rangeStart || (rangeStart && rangeEnd)) {
      // Start a new selection
      setRangeStart(dateStr);
      setRangeEnd(null);
      setHoverDate(null);
    } else {
      // Complete the selection
      if (dateStr < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(dateStr);
      } else {
        setRangeEnd(dateStr);
      }
    }
  };

  const isInRange = (day: number): boolean => {
    const dateStr = toDateStr(day);
    const start = rangeStart;
    const end = rangeEnd || hoverDate;
    if (!start || !end) return false;
    const [lo, hi] = start <= end ? [start, end] : [end, start];
    return dateStr > lo && dateStr < hi;
  };

  const isRangeStart = (day: number): boolean => {
    const dateStr = toDateStr(day);
    if (!rangeStart) return false;
    const end = rangeEnd || hoverDate;
    if (end && end < rangeStart) return dateStr === end;
    return dateStr === rangeStart;
  };

  const isRangeEnd = (day: number): boolean => {
    const dateStr = toDateStr(day);
    const end = rangeEnd || hoverDate;
    if (!rangeStart || !end) return false;
    if (end < rangeStart) return dateStr === rangeStart;
    return dateStr === end;
  };

  const calendarPrevMonth = () => {
    if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(calendarYear - 1); }
    else setCalendarMonth(calendarMonth - 1);
  };
  const calendarNextMonth = () => {
    if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(calendarYear + 1); }
    else setCalendarMonth(calendarMonth + 1);
  };
  const calendarPrevYear = () => setCalendarYear(calendarYear - 1);
  const calendarNextYear = () => setCalendarYear(calendarYear + 1);

  const calendarDays = getCalendarDays(calendarMonth, calendarYear);

  // ─── Theme color tokens ───
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBorder = isDark ? '#1D293D' : '#F1F5F9';
  const buttonBorder = isDark ? '#314158' : '#E2E8F0';
  const activeButtonBg = isDark ? '#314158' : '#E2E8F0';

  // ─── Engagement series colors (from Figma line strokes) ───
  const allEngagementColors = [
    '#FF2056',    // Likes
    '#AD46FF',    // Messages
    '#00B8DB',    // Comments
    '#00BC7D',    // Followings
    '#FD9A00',    // Posts
    '#F6339A',    // Story viewed
  ];

  const ALL_ENGAGEMENT_LEGEND = [
    { label: t('dashboard.stats.likes', 'Like'), color: isDark ? '#FFA1AD' : '#FF2056', bg: isDark ? '#4D0218' : '#FFE0E6' },
    { label: t('dashboard.stats.messages', 'Messages'), color: isDark ? '#DAB2FF' : '#AD46FF', bg: isDark ? '#3C0366' : '#FAF5FF' },
    { label: t('dashboard.stats.comments', 'Comments'), color: isDark ? '#53EAFD' : '#00B8DB', bg: isDark ? '#053345' : '#ECFFFE' },
    { label: t('dashboard.stats.followings', 'Followings'), color: isDark ? '#5EE9B5' : '#00BC7D', bg: isDark ? '#022C22' : '#ECFDF5' },
    { label: t('dashboard.stats.posts', 'Post'), color: isDark ? '#FFD230' : '#E17100', bg: isDark ? '#461901' : '#FFFBEB' },
    { label: t('dashboard.stats.storyView', 'Story View'), color: isDark ? '#FDA5D5' : '#E9117E', bg: isDark ? '#510424' : '#FFF0F7' },
  ];

  // Filter colors/legend to match visible chart series
  const visibleIndices = chartData.visibleSeriesIndices || [0, 1, 2, 3, 4, 5];
  const engagementColors = visibleIndices.map(i => allEngagementColors[i]);
  const ENGAGEMENT_LEGEND = visibleIndices.map(i => ALL_ENGAGEMENT_LEGEND[i]);

  // ─── Chart options ───
  // Shared chart grid/axis color
  const gridLine = isDark ? '#1D293D' : '#F1F5F9';
  const axisLabelColor = isDark ? '#62748E' : '#90A1B9';

  // Single-series area chart (Follower, Following)
  // Optional chartColor param lets each chart use its own accent color
  const getThemedAreaChartOptions = (categories: string[], chartColor?: string): ApexCharts.ApexOptions => {
    const color = chartColor || (isDark ? '#51A2FF' : '#2B7FFF');
    return {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'Geist, sans-serif',
      sparkline: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 2, colors: [color] },
    dataLabels: { enabled: false },
    colors: [color],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.0,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color, opacity: 0.35 },
          { offset: 100, color, opacity: 0 },
        ],
      },
    },
    xaxis: {
      categories,
      labels: { style: { colors: axisLabelColor, fontSize: '11px', fontWeight: 400 }, rotate: 0 },
      axisBorder: { show: true, color: gridLine },
      axisTicks: { show: false },
      crosshairs: { show: true, stroke: { color: isDark ? '#314158' : '#E2E8F0', width: 1, dashArray: 3 } },
    },
    yaxis: {
      labels: { style: { colors: axisLabelColor, fontSize: '11px', fontWeight: 400 } },
      min: 0,
    },
    grid: {
      borderColor: gridLine,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 8, right: 8, top: 0, bottom: 0 },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: { fontSize: '12px' },
      x: { show: true },
      marker: { show: true },
    },
    legend: { show: false },
    markers: { size: 0, hover: { size: 5, sizeOffset: 2 } },
  };
  };

  // Engagement chart (Follower / Following) — matching Views over time style
  const getEngagementChartOptions = (categories: string[], data: number[], chartColor: string, gradientId: string): ApexCharts.ApexOptions => {
    const firstVal = data[0] || 0;
    const lastVal = data[data.length - 1] || 0;
    const dataMin = Math.min(...data.filter(v => v > 0), firstVal || Infinity);
    const dataMax = Math.max(...data, lastVal);
    const dataRange = dataMax - dataMin;
    // If the range is small relative to the values, zoom the Y-axis to show differences clearly
    const yMin = dataRange > 0 && dataMin > 0 && dataRange < dataMin * 0.5
      ? Math.max(0, Math.floor(dataMin - dataRange * 0.5))
      : 0;
    const formatVal = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${v}`;
    const formatExact = (v: number) => v.toLocaleString();
    const gridColor = isDark ? '#314158' : '#E2E8F0';
    const endColor = isDark ? '#00D492' : '#009966';
    return {
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent',
        fontFamily: 'Geist, sans-serif',
        events: {
          mounted: (chartContext: { el: HTMLElement }) => {
            const svg = chartContext.el.querySelector('svg');
            if (!svg) return;
            let defs = svg.querySelector('defs');
            if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
            if (!defs.querySelector(`#${gradientId}`)) {
              const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
              grad.setAttribute('id', gradientId);
              grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
              grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
              const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', '#51A2FF');
              const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', '#00D492');
              grad.appendChild(stop1); grad.appendChild(stop2);
              defs.appendChild(grad);
            }
            const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
            paths.forEach((path) => {
              const d = path.getAttribute('d') || '';
              if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
                path.setAttribute('stroke', `url(#${gradientId})`);
              }
            });
          },
          updated: (chartContext: { el: HTMLElement }) => {
            const svg = chartContext.el.querySelector('svg');
            if (!svg) return;
            let defs = svg.querySelector('defs');
            if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
            if (!defs.querySelector(`#${gradientId}`)) {
              const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
              grad.setAttribute('id', gradientId);
              grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
              grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
              const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', '#51A2FF');
              const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', '#00D492');
              grad.appendChild(stop1); grad.appendChild(stop2);
              defs.appendChild(grad);
            }
            const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
            paths.forEach((path) => {
              const d = path.getAttribute('d') || '';
              if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
                path.setAttribute('stroke', `url(#${gradientId})`);
              }
            });
          },
        },
      },
      stroke: { curve: 'smooth', width: 2, colors: ['#51A2FF'] },
      dataLabels: { enabled: false },
      colors: ['#51A2FF'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.15,
          opacityTo: 0.0,
          stops: [0, 100],
          colorStops: [
            { offset: 0, color: '#51A2FF', opacity: 0.15 },
            { offset: 100, color: '#00D492', opacity: 0 },
          ],
        },
      },
      xaxis: {
        categories,
        labels: {
          style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
          rotate: 0,
          formatter: (val: string) => val,
        },
        axisBorder: { show: true, color: gridColor },
        axisTicks: { show: false },
        crosshairs: { show: true, stroke: { color: gridColor, width: 1, dashArray: 3 } },
      },
      yaxis: {
        labels: {
          style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
          formatter: (val: number) => formatVal(val),
        },
        min: yMin,
      },
      grid: {
        borderColor: gridColor,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 8, right: 8, top: 24, bottom: -10 },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        style: { fontSize: '12px' },
        x: { show: true },
        marker: { show: true },
      },
      legend: { show: false },
      markers: {
        size: 0,
        discrete: [
          { seriesIndex: 0, dataPointIndex: 0, fillColor: '#51A2FF', strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
          { seriesIndex: 0, dataPointIndex: data.length - 1, fillColor: '#00D492', strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
        ],
        hover: { size: 5, sizeOffset: 2 },
      },
      annotations: {
        points: [
          {
            x: categories[0],
            y: firstVal,
            marker: { size: 0 },
            label: {
              text: formatExact(firstVal),
              borderColor: '#155DFC',
              borderWidth: 1,
              borderRadius: 12,
              style: {
                color: '#8EC5FF',
                background: '#162456',
                fontSize: '14px',
                fontWeight: '500',
                padding: { left: 8, right: 8, top: 2, bottom: 2 },
              },
              offsetY: -12,
            },
          },
          {
            x: categories[categories.length - 1],
            y: lastVal,
            marker: { size: 0 },
            label: {
              text: formatExact(lastVal),
              borderColor: '#009966',
              borderWidth: 1,
              borderRadius: 12,
              style: {
                color: '#5EE9B5',
                background: '#022C22',
                fontSize: '14px',
                fontWeight: '500',
                padding: { left: 8, right: 8, top: 2, bottom: 2 },
              },
              offsetY: -12,
            },
          },
        ],
      },
    };
  };

  // Views over time chart — dedicated options matching Figma
  const getViewsChartOptions = (categories: string[], data: number[]): ApexCharts.ApexOptions => {
    const firstVal = data[0] || 0;
    const lastVal = data[data.length - 1] || 0;
    const formatK = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${v}`;
    const formatExactV = (v: number) => v.toLocaleString();
    const blueColor = isDark ? '#51A2FF' : '#2B7FFF';
    const orangeColor = isDark ? '#FFBA00' : '#E17100';
    const gridColor = isDark ? '#314158' : '#E2E8F0';
    return {
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent',
        fontFamily: 'Geist, sans-serif',
        events: {
          mounted: (chartContext: { el: HTMLElement }) => {
            const svg = chartContext.el.querySelector('svg');
            if (!svg) return;
            // Add gradient definition for the stroke line
            let defs = svg.querySelector('defs');
            if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
            if (!defs.querySelector('#viewsLineGradient')) {
              const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
              grad.setAttribute('id', 'viewsLineGradient');
              grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
              grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
              const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', blueColor);
              const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', orangeColor);
              grad.appendChild(stop1); grad.appendChild(stop2);
              defs.appendChild(grad);
            }
            // Apply gradient to the stroke path
            const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
            paths.forEach((path) => {
              const d = path.getAttribute('d') || '';
              if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
                path.setAttribute('stroke', 'url(#viewsLineGradient)');
              }
            });
          },
          updated: (chartContext: { el: HTMLElement }) => {
            const svg = chartContext.el.querySelector('svg');
            if (!svg) return;
            let defs = svg.querySelector('defs');
            if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
            if (!defs.querySelector('#viewsLineGradient')) {
              const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
              grad.setAttribute('id', 'viewsLineGradient');
              grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
              grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
              const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', blueColor);
              const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', orangeColor);
              grad.appendChild(stop1); grad.appendChild(stop2);
              defs.appendChild(grad);
            }
            const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
            paths.forEach((path) => {
              const d = path.getAttribute('d') || '';
              if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
                path.setAttribute('stroke', 'url(#viewsLineGradient)');
              }
            });
          },
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: [blueColor],
      },
      dataLabels: { enabled: false },
      colors: [blueColor],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.15,
          opacityTo: 0.0,
          stops: [0, 100],
          colorStops: [
            { offset: 0, color: blueColor, opacity: 0.15 },
            { offset: 100, color: blueColor, opacity: 0 },
          ],
        },
      },
      xaxis: {
        categories,
        labels: {
          style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
          rotate: 0,
          formatter: (val: string) => val,
        },
        axisBorder: { show: true, color: gridColor },
        axisTicks: { show: false },
        crosshairs: { show: true, stroke: { color: gridColor, width: 1, dashArray: 3 } },
      },
      yaxis: {
        labels: {
          style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
          formatter: (val: number) => formatK(val),
        },
        min: 0,
      },
      grid: {
        borderColor: gridColor,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 8, right: 8, top: 24, bottom: -10 },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        style: { fontSize: '12px' },
        x: { show: true },
        marker: { show: true },
      },
      legend: { show: false },
      markers: {
        size: 0,
        discrete: [
          { seriesIndex: 0, dataPointIndex: 0, fillColor: blueColor, strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
          { seriesIndex: 0, dataPointIndex: data.length - 1, fillColor: orangeColor, strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
        ],
        hover: { size: 5, sizeOffset: 2 },
      },
      annotations: {
        points: [
          {
            x: categories[0],
            y: firstVal,
            marker: { size: 0 },
            label: {
              text: formatExactV(firstVal),
              borderColor: isDark ? '#155DFC' : '#2B7FFF',
              borderWidth: 1,
              borderRadius: 12,
              style: {
                color: isDark ? '#8EC5FF' : '#2B7FFF',
                background: isDark ? '#162456' : '#EFF6FF',
                fontSize: '14px',
                fontWeight: '500',
                padding: { left: 8, right: 8, top: 2, bottom: 2 },
              },
              offsetY: -12,
            },
          },
          {
            x: categories[categories.length - 1],
            y: lastVal,
            marker: { size: 0 },
            label: {
              text: formatExactV(lastVal),
              borderColor: isDark ? '#E17100' : '#FFD230',
              borderWidth: 1,
              borderRadius: 12,
              style: {
                color: isDark ? '#FFD230' : '#E17100',
                background: isDark ? '#461901' : '#FFFBEB',
                fontSize: '14px',
                fontWeight: '500',
                padding: { left: 8, right: 8, top: 2, bottom: 2 },
              },
              offsetY: -12,
            },
          },
        ],
      },
    };
  };

  // Multi-series area chart (Engagement over time)
  const engagementGridColor = isDark ? '#314158' : '#E2E8F0';
  const engagementAxisColor = isDark ? '#90A1B9' : '#62748E';
  const mainChartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
      selection: { enabled: true },
      background: 'transparent',
      fontFamily: 'Geist, sans-serif',
      id: 'engagement-over-time',
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    colors: engagementColors,
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.1,
        opacityTo: 0.0,
        stops: [0, 100],
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: { style: { colors: engagementAxisColor, fontSize: '14px', fontWeight: 400 }, rotate: 0 },
      axisBorder: { show: true, color: engagementGridColor },
      axisTicks: { show: true, color: engagementGridColor, height: 8 },
      crosshairs: { show: true, stroke: { color: engagementGridColor, width: 1, dashArray: 3 } },
    },
    yaxis: {
      labels: { style: { colors: engagementAxisColor, fontSize: '14px', fontWeight: 400 } },
      min: 0,
    },
    grid: {
      borderColor: engagementGridColor,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 8, right: 8, top: 0, bottom: 0 },
    },
    legend: { show: false },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ series, seriesIndex: _si, dataPointIndex, w }: { series: number[][]; seriesIndex: number; dataPointIndex: number; w: { globals: { seriesNames: string[]; colors: string[] }; config: { xaxis: { categories: string[] } } } }) => {
        const allNames = [t('dashboard.stats.likes'), t('dashboard.stats.messages'), t('dashboard.stats.comments'), t('dashboard.stats.followings'), t('dashboard.stats.posts'), t('dashboard.stats.storyView')];
        const seriesNames = visibleIndices.map(i => allNames[i]);
        const rows = series.map((s: number[], i: number) => {
          const val = s[dataPointIndex] ?? 0;
          return `<div style="display:flex;align-items:center;gap:8px;padding:0;"><div style="width:6px;height:0;border:2px solid ${w.globals.colors[i]};border-radius:1px;"></div><span style="flex:1;font-size:14px;font-weight:400;color:#90A1B9;">${seriesNames[i] || w.globals.seriesNames[i]}</span><span style="font-size:14px;font-weight:700;color:#E2E8F0;text-align:right;">${val}</span></div>`;
        }).join('');
        return `<div style="background:#0F172B;border:1px solid #1D293D;border-radius:8px;box-shadow:0px 10px 15px -3px rgba(2,6,24,0.5),0px 4px 6px -2px rgba(0,0,0,0.05);padding:16px;display:flex;flex-direction:column;gap:4px;min-width:160px;">${rows}</div>`;
      },
    },
    markers: { size: 0, hover: { size: 4, sizeOffset: 2 } },
  };

  // Radial donut chart (Daily Action Usage) — full circle
  const radialChartOptions: ApexCharts.ApexOptions = {
    chart: { height: 200, type: 'radialBar', background: 'transparent', fontFamily: 'Geist, sans-serif' },
    plotOptions: {
      radialBar: {
        startAngle: 0,
        endAngle: 360,
        hollow: { size: '68%', background: 'transparent' },
        track: {
          background: isDark ? '#314158' : '#E2E8F0',
          strokeWidth: '100%',
        },
        dataLabels: {
          show: true,
          name: { show: true, fontSize: '14px', fontWeight: 400, color: subtitleColor, offsetY: 24 },
          value: {
            show: true,
            fontSize: '36px',
            fontWeight: 700,
            color: titleColor,
            offsetY: -10,
            formatter: (val: number) => `${Math.round(val)}`,
          },
        },
      },
    },
    labels: [t('dashboard.charts.used', 'used')],
    stroke: { lineCap: 'round' },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'horizontal',
        gradientToColors: ['#00D492'],
        colorStops: [
          { offset: 0, color: '#2B7FFF', opacity: 1 },
          { offset: 100, color: '#00D492', opacity: 1 },
        ],
      },
    },
  };

  // ─── Date filter labels ───
  const dateFilters = [
    t('dashboard.dateFilters.todays'),
    t('dashboard.dateFilters.last7Days'),
    t('dashboard.dateFilters.last30Days'),
  ];

  // ─── Compute total views: use instagram_views if available, otherwise sum action data ───
  const totalViews = instagramViews != null
    ? instagramViews
    : (insightsCharts.analysisData?.data?.reduce((s, v) => s + v, 0) || 0);
  const totalViewsFormatted = totalViews >= 1000 ? (totalViews / 1000).toFixed(1) : String(totalViews);
  const totalViewsSuffix = totalViews >= 1000 ? 'K' : '';

  // Analysis badge display values
  const analysisBadgeColorKey = analysisBadge?.showFire ? 'fire' : analysisBadge?.direction === 'up' ? 'up' : 'none';
  const analysisBadgeColors = BADGE_COLORS[analysisBadgeColorKey][isDark ? 'dark' : 'light'];
  const analysisBadgeValue = analysisBadge?.value || 0;
  const analysisBadgeFormatted = Math.abs(analysisBadgeValue) >= 1000 ? (Math.abs(analysisBadgeValue) / 1000).toFixed(1) : String(Math.abs(analysisBadgeValue));
  const analysisBadgeSuffix = Math.abs(analysisBadgeValue) >= 1000 ? 'K' : '';
  const AnalysisTrendIcon = analysisBadge?.direction === 'down' ? ChevronsDown : ChevronsUp;

  // ─── Early returns ───
  if (isLoading) return <Spinner size="large" text={t('dashboard.loading.dashboardData')} fullPage />;

  if (accounts.length === 0) {
    navigate('/accounts', { state: { autoOpenOverlay: true }, replace: true });
    return null;
  }

  if (rulesCount === 0 && !isReauthOverlayOpen) {
    const acct = selectedAccount ? { _id: selectedAccount._id, username: selectedAccount.username, name: selectedAccount.name, profile_picture_base64: selectedAccount.imgSrc } : null;
    const allAccts = accounts.map(a => ({ _id: a._id, username: a.username, name: a.name, profile_picture_base64: a.imgSrc }));
    navigate('/rules/new', { replace: true, state: { selectedAccount: acct, accounts: allAccts, fromDashboard: true } });
    return null;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full" style={{ background: containerBg, padding: '24px' }}>
        <div className="flex flex-col items-center" style={{ background: mainBg, borderRadius: '16px', border: `1px solid ${cardBorder}`, padding: '32px', maxWidth: '400px', textAlign: 'center' }}>
          <div className="flex items-center justify-center" style={{ width: '64px', height: '64px', background: isDark ? '#4D0218' : '#FEF2F2', borderRadius: '50%', marginBottom: '16px' }}>
            <AlertTriangle size={24} color={isDark ? '#FF637E' : '#EF4444'} />
          </div>
          <span style={{ fontSize: '16px', fontWeight: 600, color: titleColor, marginBottom: '8px' }}>{t('dashboard.errors.errorLoadingDashboard')}</span>
          <span style={{ fontSize: '14px', color: subtitleColor, marginBottom: '16px' }}>{error}</span>
          <button
            className="transition-opacity hover:opacity-90"
            style={{ padding: '8px 16px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', color: '#FFFFFF', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}
            onClick={() => { setError(null); if (selectedAccount) refreshData(selectedAccount._id); else if (accounts.length > 0) refreshData(accounts[0]._id); else navigate('/accounts'); }}
          >
            {accounts.length > 0 ? t('dashboard.actions.retry') : t('dashboard.actions.addAccount')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ gap: '8px', background: containerBg }}
    >
      <div
        className="flex flex-col flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow }}
      >
        {/* ─── Header ─── */}
        <div
          className="flex flex-col sm:flex-row sm:items-center flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{ padding: '12px 16px', gap: '8px', borderBottom: `1px solid ${headerBorder}`, background: mainBg }}
        >
          {/* Row 1: Hamburger/Icon + Title (on desktop: + dropdown + filters) */}
          <div className="flex items-center" style={{ gap: '8px', flex: 1, minWidth: 0 }}>
            {/* Mobile: hamburger button */}
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            {/* Desktop: icon */}
            <div className="hidden lg:flex items-center justify-center flex-shrink-0" style={{ width: '36px', height: '36px', background: isDark ? '#162456' : '#EFF6FF', borderRadius: '12px' }}>
              <LayoutDashboard size={24} color={isDark ? '#51A2FF' : '#2B7FFF'} />
            </div>
            <span style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, flex: 1 }}>
              {t('dashboard.title')}
            </span>

            {/* Account dropdown — desktop only (inline) */}
            <div ref={dropdownRef} className="hidden sm:block" style={{ position: 'relative' }}>
              <button
                className="flex items-center transition-opacity hover:opacity-80"
                style={{ padding: '8px 12px', height: '36px', width: '160px', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', cursor: 'pointer', gap: '4px' }}
                onClick={toggleDropdown}
              >
                {selectedAccount && (
                  <>
                    <img src={selectedAccount.imgSrc} alt={selectedAccount.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                    <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{selectedAccount.username}</span>
                  </>
                )}
                {accounts.length > 1 && <ChevronDown size={20} strokeWidth={3} color={isDark ? '#CAD5E2' : '#45556C'} style={{ flexShrink: 0 }} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />}
              </button>
              {isDropdownOpen && accounts.length > 1 && (
                <div
                  style={{ position: 'absolute', right: 0, top: '40px', width: '220px', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', zIndex: 50, maxHeight: '240px', overflowY: 'auto' }}
                >
                  {accounts.map((account) => (
                    <div
                      key={account._id}
                      className="flex items-center cursor-pointer transition-all"
                      style={{ padding: '8px 12px', gap: '8px', background: selectedAccount?._id === account._id ? (isDark ? '#162456' : '#EFF6FF') : 'transparent' }}
                      onMouseEnter={(e) => { if (selectedAccount?._id !== account._id) e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC'; }}
                      onMouseLeave={(e) => { if (selectedAccount?._id !== account._id) e.currentTarget.style.background = 'transparent'; }}
                      onClick={() => handleAccountSelect(account)}
                    >
                      <img src={account.imgSrc} alt={account.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover' }} />
                      <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor, flex: 1 }}>{account.username}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Row 2 (mobile only): Account dropdown */}
          <div ref={mobileDropdownRef} className="sm:hidden" style={{ position: 'relative' }}>
            <button
              className="flex items-center transition-opacity hover:opacity-80 w-full"
              style={{ padding: '8px 12px', height: '36px', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', cursor: 'pointer', gap: '4px' }}
              onClick={toggleDropdown}
            >
              {selectedAccount && (
                <>
                  <img src={selectedAccount.imgSrc} alt={selectedAccount.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textAlign: 'left' }}>{selectedAccount.username}</span>
                </>
              )}
              {accounts.length > 1 && <ChevronDown size={20} strokeWidth={3} color={isDark ? '#CAD5E2' : '#45556C'} style={{ flexShrink: 0 }} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />}
            </button>
            {isDropdownOpen && accounts.length > 1 && (
              <div
                style={{ position: 'absolute', left: 0, top: '40px', width: '100%', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', zIndex: 50, maxHeight: '240px', overflowY: 'auto' }}
              >
                {accounts.map((account) => (
                  <div
                    key={account._id}
                    className="flex items-center cursor-pointer transition-all"
                    style={{ padding: '8px 12px', gap: '8px', background: selectedAccount?._id === account._id ? (isDark ? '#162456' : '#EFF6FF') : 'transparent' }}
                    onMouseEnter={(e) => { if (selectedAccount?._id !== account._id) e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC'; }}
                    onMouseLeave={(e) => { if (selectedAccount?._id !== account._id) e.currentTarget.style.background = 'transparent'; }}
                    onClick={() => handleAccountSelect(account)}
                  >
                    <img src={account.imgSrc} alt={account.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover' }} />
                    <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor, flex: 1 }}>{account.username}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Row 3 on mobile / Row 2 on desktop: Date filters + Calendar */}
          <div className="flex items-center" style={{ gap: '8px' }}>
            {/* Segmented date filter buttons */}
            <div className="flex items-center flex-1" style={{ border: `1px solid ${buttonBorder}`, borderRadius: '8px', overflow: 'hidden', height: '36px' }}>
              {dateFilters.map((label, idx) => (
                <button
                  key={label}
                  className="transition-all flex-1"
                  style={{
                    padding: '8px 8px',
                    background: activeButton === label ? activeButtonBg : mainBg,
                    border: 'none',
                    borderRight: idx < dateFilters.length - 1 ? `1px solid ${buttonBorder}` : 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    lineHeight: '20px',
                    fontWeight: 500,
                    color: titleColor,
                    height: '100%',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleButtonClick(label)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Calendar icon button with date range picker */}
            <div ref={datePickerRef} style={{ position: 'relative' }}>
              <button
                className="flex items-center justify-center transition-opacity hover:opacity-80"
                style={{
                  width: '36px', height: '36px', padding: '8px',
                  background: activeButton === t('dashboard.dateFilters.custom') ? activeButtonBg : mainBg,
                  border: `1px solid ${buttonBorder}`, borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
                }}
                onClick={() => {
                  setShowDatePicker(!showDatePicker);
                  if (!showDatePicker) {
                    const now = new Date();
                    setCalendarMonth(now.getMonth());
                    setCalendarYear(now.getFullYear());
                  }
                }}
              >
                <CalendarDays size={20} color={titleColor} />
              </button>
            {showDatePicker && (
              <div
                style={{
                  position: 'absolute', right: 0, top: '40px',
                  width: '296px', height: '296px',
                  background: isDark ? '#0F172B' : '#FFFFFF',
                  border: `1px solid ${isDark ? '#1D293D' : '#E2E8F0'}`,
                  boxShadow: isDark
                    ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    : '0px 10px 15px -3px rgba(43, 127, 255, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  borderRadius: '16px',
                  padding: '8px',
                  zIndex: 50,
                  display: 'flex', flexDirection: 'row', alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '280px', height: '280px' }}>
                  {/* Top navigation */}
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '280px', height: '36px' }}>
                    <button onClick={calendarPrevYear} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '36px', height: '36px', background: 'rgba(255,255,255,0.01)', borderRadius: '8px', border: 'none', cursor: 'pointer', padding: '8px' }}>
                      <ChevronsLeft size={20} color={isDark ? '#E2E8F0' : '#314158'} />
                    </button>
                    <button onClick={calendarPrevMonth} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '36px', height: '36px', background: 'rgba(255,255,255,0.01)', borderRadius: '8px', border: 'none', cursor: 'pointer', padding: '8px' }}>
                      <ChevronLeft size={20} color={isDark ? '#E2E8F0' : '#314158'} />
                    </button>
                    <span style={{ flex: 1, fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '24px', textAlign: 'center', color: isDark ? '#E2E8F0' : '#314158' }}>
                      {MONTH_NAMES[calendarMonth]} {calendarYear}
                    </span>
                    <button onClick={calendarNextMonth} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '36px', height: '36px', background: 'rgba(255,255,255,0.01)', borderRadius: '8px', border: 'none', cursor: 'pointer', padding: '8px' }}>
                      <ChevronRight size={20} color={isDark ? '#E2E8F0' : '#314158'} />
                    </button>
                    <button onClick={calendarNextYear} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '36px', height: '36px', background: 'rgba(255,255,255,0.01)', borderRadius: '8px', border: 'none', cursor: 'pointer', padding: '8px' }}>
                      <ChevronsRight size={20} color={isDark ? '#E2E8F0' : '#314158'} />
                    </button>
                  </div>

                  {/* Day labels + grid container */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '16px', width: '280px' }}>
                    {/* Day-of-week headers */}
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '280px', height: '28px', paddingBottom: '4px' }}>
                      {DAY_LABELS.map((label, i) => (
                        <span key={i} style={{ width: '40px', fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '24px', textAlign: 'center', color: isDark ? '#90A1B9' : '#90A1B9' }}>
                          {label}
                        </span>
                      ))}
                    </div>

                    {/* Calendar grid */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '280px' }}>
                      {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, weekIdx) => (
                        <div key={weekIdx} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '280px', height: '40px' }}>
                          {calendarDays.slice(weekIdx * 7, weekIdx * 7 + 7).map((day, dayIdx) => {
                            if (day === null) {
                              return <div key={dayIdx} style={{ width: '40px', height: '40px' }} />;
                            }
                            const dateStr = toDateStr(day);
                            const isStart = isRangeStart(day);
                            const isEnd = isRangeEnd(day);
                            const inRange = isInRange(day);
                            const isSelected = isStart || isEnd;
                            const today = new Date();
                            const isToday = day === today.getDate() && calendarMonth === today.getMonth() && calendarYear === today.getFullYear();

                            // Determine the highlight background behind the selected circle
                            // For start: half highlight on the right side; for end: half highlight on left side
                            const hasRange = rangeStart && (rangeEnd || hoverDate);
                            const effectiveEnd = rangeEnd || hoverDate;
                            const hasBothEndpoints = rangeStart && effectiveEnd && rangeStart !== effectiveEnd;

                            return (
                              <div
                                key={dayIdx}
                                style={{
                                  width: '40px', height: '40px',
                                  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                                  position: 'relative', isolation: 'isolate',
                                  cursor: 'pointer',
                                  background: inRange ? (isDark ? '#314158' : '#E2E8F0') : 'transparent',
                                }}
                                onClick={() => handleCalendarDayClick(day)}
                                onMouseEnter={() => { if (rangeStart && !rangeEnd) setHoverDate(dateStr); }}
                                onMouseLeave={() => { if (rangeStart && !rangeEnd) setHoverDate(null); }}
                              >
                                {/* Half-highlight behind start/end circles */}
                                {isStart && hasBothEndpoints && (
                                  <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '20px', background: isDark ? '#314158' : '#E2E8F0', zIndex: 0 }} />
                                )}
                                {isEnd && hasBothEndpoints && (
                                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '20px', background: isDark ? '#314158' : '#E2E8F0', zIndex: 0 }} />
                                )}
                                {/* Day circle */}
                                <div
                                  style={{
                                    width: '40px', height: '40px',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                                    borderRadius: '999px',
                                    background: isSelected ? (isDark ? '#E2E8F0' : '#314158') : 'transparent',
                                    zIndex: 1,
                                  }}
                                >
                                  <span style={{
                                    fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '24px',
                                    textAlign: 'center',
                                    color: isSelected
                                      ? (isDark ? '#0F172B' : '#FFFFFF')
                                      : (isDark ? '#E2E8F0' : '#314158'),
                                  }}>
                                    {day}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* ─── Scrollable content ─── */}
        <div
          className={`flex-1 min-h-0 overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
          style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          {/* ─── Overview Section ─── */}
          <div className="flex flex-col" style={{ gap: '16px' }}>
            <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>
              {t('dashboard.charts.overview', 'Overview')}
            </span>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7" style={{ gap: '8px' }}>
              {[...stats.map((stat, idx) => ({ stat, idx }))]
                .sort((a, b) => (a.stat.disabled ? 1 : 0) - (b.stat.disabled ? 1 : 0))
                .map(({ stat, idx }) => {
                if (stat.hidden) return null;
                const config = OVERVIEW_CARD_CONFIG[idx];
                const CardIcon = config.icon;
                const theme = isDark ? 'dark' : 'light';
                const badge = stat.badge;
                const badgeColorKey = badge?.showFire ? 'fire' : badge?.direction === 'up' ? 'up' : 'none';
                const tagColors = BADGE_COLORS[badgeColorKey][theme];
                const TrendIcon = badge?.direction === 'down' ? ChevronsDown : ChevronsUp;
                const isDisabled = stat.disabled;
                const disabledColor = isDark ? '#475569' : '#94A3B8';
                return (
                  <div
                    key={stat.label}
                    className={`transition-opacity ${isDisabled ? 'cursor-default' : 'cursor-pointer hover:opacity-90'}`}
                    style={{
                      background: isDisabled
                        ? (isDark ? '#1E293B' : '#E2E8F0')
                        : `linear-gradient(56.67deg, ${isDark ? '#1E293B' : '#E2E8F0'} 49.49%, ${config.borderColor[theme]} 99.89%)`,
                      borderRadius: '16px',
                      padding: '1px',
                      opacity: isDisabled ? 0.75 : undefined,
                      pointerEvents: isDisabled ? 'none' : undefined,
                    }}
                    onClick={() => !isDisabled && handleStatClick(stat.route)}
                  >
                    <div
                      className="flex flex-col"
                      style={{
                        background: isDisabled
                          ? mainBg
                          : `linear-gradient(56.67deg, ${mainBg} 49.49%, ${config.gradientAccent[theme]} 99.89%)`,
                        borderRadius: '15px',
                        padding: '12px',
                        gap: '8px',
                        height: '100%',
                      }}
                    >
                      {/* Top row: Tag + Icon */}
                      <div className="flex items-center justify-between">
                        {idx > 0 && badge && badge.direction !== 'none' && !isDisabled ? (
                          <span
                            className="flex items-center"
                            style={{
                              fontSize: '14px', lineHeight: '20px', fontWeight: 500,
                              background: tagColors.bg, border: `1px solid ${tagColors.border}`, color: tagColors.color,
                              borderRadius: '999px', padding: '2px 6px', gap: '2px',
                            }}
                          >
                            <TrendIcon size={20} color={tagColors.chevron} />
                            {badge.direction === 'down' ? `${badge.value}` : `+${badge.value}`}
                            {badge.showFire && <span style={{ fontSize: '14px', lineHeight: 1 }}>🔥</span>}
                          </span>
                        ) : <span />}
                        <CardIcon size={24} color={isDisabled ? disabledColor : config.iconColor[theme]} />
                      </div>
                      {/* Bottom: Number + Label */}
                      <div className="flex flex-col" style={{ gap: '2px' }}>
                        <div className="flex items-end" style={{ gap: '4px' }}>
                          <span className="text-xl sm:text-3xl" style={{ lineHeight: '36px', fontWeight: 700, color: isDisabled ? disabledColor : titleColor }}>
                            {stat.count}
                          </span>
                          {stat.suffix && (
                            <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: subtitleColor }}>
                              {stat.suffix}
                            </span>
                          )}
                        </div>
                        <span className="text-xs sm:text-base" style={{ lineHeight: '20px', fontWeight: 500, color: isDisabled ? disabledColor : subtitleColor }}>
                          {stat.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ─── Notifications Section ─── */}
          {(selectedAccount?.is_locked || showNotification || (showNextJobNotification && !(selectedAccount?.all_jobs_completed) && !showSleepingNotification) || showSleepingNotification || (showAllJobsCompletedNotification && selectedAccount?.all_jobs_completed && !selectedAccount?.is_sleeping)) && (
            <div className="flex flex-col" style={{ gap: '8px' }}>
              {/* Account locked notification */}
              {selectedAccount?.is_locked && (() => {
                const lockLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
                const hasLockInfo = !!(selectedAccount.lock_title?.en && selectedAccount.lock_title?.fr && selectedAccount.lock_description?.en && selectedAccount.lock_description?.fr);
                const lockTitleText = hasLockInfo
                  ? (lockLang === 'fr' ? selectedAccount.lock_title!.fr : selectedAccount.lock_title!.en)
                  : t('dashboard.notifications.accountLocked', 'Account Locked');
                const lockDescText = hasLockInfo
                  ? (lockLang === 'fr' ? selectedAccount.lock_description!.fr : selectedAccount.lock_description!.en)
                  : t('dashboard.notifications.accountLockedMessage', 'Your Instagram account is locked. Please reconnect to resume all actions.');
                return (
                  <div className="flex items-start" style={{ background: isDark ? '#2D0A13' : '#FFF1F2', borderLeft: `4px solid ${isDark ? '#FB7185' : '#EC003F'}`, borderRadius: '8px', padding: '16px', gap: '12px', position: 'relative' }}>
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: '32px', height: '32px', background: mainBg, borderRadius: '50%' }}>
                      <Lock size={16} color={isDark ? '#FDA4AF' : '#EC003F'} />
                    </div>
                    <div className="flex flex-col flex-1" style={{ gap: '8px' }}>
                      <div className="flex flex-col" style={{ gap: '4px' }}>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>{lockTitleText}</span>
                        <span style={{ fontSize: '14px', color: subtitleColor }}>{lockDescText}</span>
                      </div>
                      <button
                        className="flex items-center self-start transition-opacity hover:opacity-90"
                        style={{
                          padding: '6px 14px', gap: '6px',
                          background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                          borderRadius: '8px', border: 'none', cursor: 'pointer',
                        }}
                        onClick={() => {
                          setReauthUsername(selectedAccount?.username);
                          setReauthProfilePic(selectedAccount?.imgSrc);
                          setIsReauthOverlayOpen(true);
                        }}
                      >
                        <RotateCcw size={14} color="#FFFFFF" />
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }}>{t('dashboard.notifications.reconnect', 'Reconnect')}</span>
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Freeze / Paused notification */}
              {showNotification && hoursRemaining !== null && (
                <div className="flex items-start" style={{ background: isDark ? '#162456' : '#EFF6FF', borderLeft: `4px solid ${isDark ? '#2B7FFF' : '#2B7FFF'}`, borderRadius: '8px', padding: '16px', gap: '12px', position: 'relative' }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: '32px', height: '32px', background: mainBg, borderRadius: '50%' }}>
                    <Info size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} />
                  </div>
                  <div className="flex flex-col flex-1" style={{ gap: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>{t('dashboard.notifications.actionScheduleUpdate')}</span>
                    <span style={{ fontSize: '14px', color: subtitleColor }}>
                      {isMinutesFreeze ? t('dashboard.notifications.pausedForMinutes', { count: hoursRemaining }) : t('dashboard.notifications.pausedForHours', { count: hoursRemaining })}
                    </span>
                  </div>
                  <button className="flex items-center justify-center transition-opacity hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, position: 'absolute', top: '16px', right: '16px' }} onClick={handleCloseNotification}>
                    <X size={16} color={subtitleColor} />
                  </button>
                </div>
              )}

              {/* Next job scheduled notification (hide during sleeping hours) */}
              {showNextJobNotification && nextJobHoursRemaining !== null && !(selectedAccount?.all_jobs_completed) && !showSleepingNotification && (
                <div className="flex items-start" style={{ background: isDark ? '#162456' : '#EFF6FF', borderLeft: `4px solid ${isDark ? '#2B7FFF' : '#2B7FFF'}`, borderRadius: '8px', padding: '16px', gap: '12px', position: 'relative' }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: '32px', height: '32px', background: mainBg, borderRadius: '50%' }}>
                    <Bell size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} />
                  </div>
                  <div className="flex flex-col flex-1" style={{ gap: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>{t('dashboard.notifications.nextJobScheduled')}</span>
                    <span style={{ fontSize: '14px', color: subtitleColor }}>
                      {isMinutesNextJob ? t('dashboard.notifications.resumeInMinutes', { count: nextJobHoursRemaining }) : t('dashboard.notifications.resumeInHours', { count: nextJobHoursRemaining })}
                    </span>
                    <span style={{ fontSize: '12px', color: subtitleColor }}>{formatLastRunTime(selectedAccount?.last_job_run)}</span>
                  </div>
                  <button className="flex items-center justify-center transition-opacity hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, position: 'absolute', top: '16px', right: '16px' }} onClick={handleCloseNextJobNotification}>
                    <X size={16} color={subtitleColor} />
                  </button>
                </div>
              )}

              {/* Sleeping hours notification */}
              {showSleepingNotification && (
                <div className="flex items-start" style={{ background: isDark ? '#1C1033' : '#FAF5FF', borderLeft: `4px solid ${isDark ? '#AD46FF' : '#AD46FF'}`, borderRadius: '8px', padding: '16px', gap: '12px', position: 'relative' }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: '32px', height: '32px', background: mainBg, borderRadius: '50%' }}>
                    <Moon size={16} color={isDark ? '#C27AFF' : '#AD46FF'} />
                  </div>
                  <div className="flex flex-col flex-1" style={{ gap: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>{t('dashboard.notifications.sleepingHours', 'Sleeping Hours')}</span>
                    <span style={{ fontSize: '14px', color: subtitleColor }}>{t('dashboard.notifications.sleepingHoursMessage', 'Your account is currently in sleeping hours (11 PM - 6 AM). Actions will resume automatically when sleeping hours end.')}</span>
                  </div>
                  <button className="flex items-center justify-center transition-opacity hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, position: 'absolute', top: '16px', right: '16px' }} onClick={() => setShowSleepingNotification(false)}>
                    <X size={16} color={subtitleColor} />
                  </button>
                </div>
              )}

              {/* All jobs completed notification (only when NOT sleeping) */}
              {showAllJobsCompletedNotification && selectedAccount?.all_jobs_completed && !selectedAccount?.is_sleeping && (
                <div className="flex items-start" style={{ background: isDark ? '#022C22' : '#ECFDF5', borderLeft: `4px solid ${isDark ? '#00BC7D' : '#00BC7D'}`, borderRadius: '8px', padding: '16px', gap: '12px', position: 'relative' }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: '32px', height: '32px', background: mainBg, borderRadius: '50%' }}>
                    <CheckCircle2 size={16} color={isDark ? '#5EE9B5' : '#009966'} />
                  </div>
                  <div className="flex flex-col flex-1" style={{ gap: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>{t('dashboard.notifications.allJobsCompleted')}</span>
                    <span style={{ fontSize: '14px', color: subtitleColor }}>{t('dashboard.notifications.finishedForDay')}</span>
                  </div>
                  <button className="flex items-center justify-center transition-opacity hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, position: 'absolute', top: '16px', right: '16px' }} onClick={() => setShowAllJobsCompletedNotification(false)}>
                    <X size={16} color={subtitleColor} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ─── Analysis Section ─── */}
          <div className="flex flex-col" style={{ gap: '16px' }}>
            <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>
              {t('dashboard.charts.analysisDashboard', 'Analysis')}
            </span>
            <div className="flex flex-col lg:flex-row" style={{ gap: '16px' }}>
              {/* Left column: Total Views + Daily Action Usage */}
              <div className="flex flex-col w-full lg:max-w-[400px] lg:flex-shrink-0" style={{ gap: '8px' }}>
                {/* Total Views card */}
                <div style={{ background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-lg" style={{ lineHeight: '28px', fontWeight: 500, color: subtitleColor }}>
                      {t('dashboard.charts.totalViews', 'Total Views (last 30 days)')}
                    </span>
                    {analysisBadge && analysisBadge.direction !== 'none' && (
                      <span
                        className="flex items-center"
                        style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, background: analysisBadgeColors.bg, border: `1px solid ${analysisBadgeColors.border}`, color: analysisBadgeColors.color, borderRadius: '999px', padding: '4px 6px', gap: '2px' }}
                      >
                        <AnalysisTrendIcon size={20} color={analysisBadgeColors.chevron} />
                        {analysisBadge.direction === 'down' ? `-${analysisBadgeFormatted}${analysisBadgeSuffix}` : `+${analysisBadgeFormatted}${analysisBadgeSuffix}`}
                        {analysisBadge.showFire && <span style={{ fontSize: '14px', lineHeight: 1 }}>🔥</span>}
                      </span>
                    )}
                  </div>
                  <div className="flex items-end justify-end" style={{ gap: '4px' }}>
                    <span style={{ fontSize: '36px', lineHeight: '40px', fontWeight: 700, color: titleColor }}>{totalViewsFormatted}</span>
                    {totalViewsSuffix && <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, color: subtitleColor }}>{totalViewsSuffix}</span>}
                  </div>
                </div>

                {/* Daily Action Usage card */}
                <div style={{ background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-lg" style={{ lineHeight: '28px', fontWeight: 500, color: subtitleColor }}>
                      {t('dashboard.charts.dailyLimitUtilized', 'Daily Action Usage')}
                    </span>
                    {selectedAccount?.is_active && !selectedAccount?.is_locked ? (
                      <span
                        style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, background: isDark ? '#022C22' : '#ECFDF5', border: `1px solid ${isDark ? '#009966' : '#5EE9B5'}`, color: isDark ? '#5EE9B5' : '#009966', borderRadius: '999px', padding: '4px 8px' }}
                      >
                        {t('rules.status.active', 'Active')}
                      </span>
                    ) : (
                      <span
                        style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, background: isDark ? '#2D0A13' : '#FFF1F2', border: `1px solid ${isDark ? '#F87171' : '#FECDD3'}`, color: isDark ? '#FCA5A5' : '#DC2626', borderRadius: '999px', padding: '4px 8px' }}
                      >
                        {t('rules.status.inactive', 'Inactive')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-center" style={{ position: 'relative' }}>
                    <Chart
                      key={`radial-${isDark ? 'dark' : 'light'}`}
                      options={radialChartOptions}
                      series={[progressPercentage]}
                      type="radialBar"
                      height={200}
                      width={200}
                    />
                    <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(calc(-50% + 28px), calc(-50% - 4px))', fontSize: '16px', fontWeight: 400, color: subtitleColor }}>%</span>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between" style={{ padding: '8px' }}>
                      <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, color: subtitleColor }}>{t('dashboard.messages.actionsTarget', 'Daily limit')}</span>
                      <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>{achievedValue.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between" style={{ padding: '8px' }}>
                      <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, color: subtitleColor }}>{t('dashboard.messages.actionsAchieved', 'Actions used')}</span>
                      <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>{targetValue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column: Views over time chart */}
              <div className="min-w-0" style={{ background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" style={{ gap: '8px' }}>
                  <div className="flex items-center" style={{ gap: '4px' }}>
                    <span className="text-sm sm:text-lg" style={{ lineHeight: '28px', fontWeight: 500, color: subtitleColor }}>
                      {t('dashboard.charts.viewsOverTime', 'Views over time (last 30 days)')}
                    </span>
                    <Info size={16} color={subtitleColor} />
                  </div>
                  {analysisBadge && analysisBadge.direction !== 'none' && (
                    <span
                      className="flex items-center"
                      style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, background: analysisBadgeColors.bg, border: `1px solid ${analysisBadgeColors.border}`, color: analysisBadgeColors.color, borderRadius: '999px', padding: '4px 6px', gap: '2px' }}
                    >
                      <AnalysisTrendIcon size={20} color={analysisBadgeColors.chevron} />
                      {analysisBadge.direction === 'down' ? `-${analysisBadgeFormatted}${analysisBadgeSuffix}` : `+${analysisBadgeFormatted}${analysisBadgeSuffix}`}
                      {analysisBadge.showFire && <span style={{ fontSize: '14px', lineHeight: 1 }}>🔥</span>}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  {insightsCharts.analysisData?.categories?.length > 0 && insightsCharts.analysisData?.data?.length > 0 ? (
                    <Chart
                      key={`analysis-${isDark ? 'dark' : 'light'}`}
                      options={getViewsChartOptions(insightsCharts.analysisData.categories, insightsCharts.analysisData.data)}
                      series={[{ name: t('dashboard.charts.analysis', 'Views'), data: insightsCharts.analysisData.data }]}
                      type="area"
                      height={300}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center" style={{ padding: '48px 0' }}>
                      <span style={{ fontSize: '14px', color: subtitleColor }}>{t('dashboard.emptyStates.noAnalysisData')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ─── Engagement Overview Section ─── */}
          <div className="flex flex-col" style={{ gap: '16px' }}>
            <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>
              {t('dashboard.charts.engagementDashboard', 'Engagement overview')}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '16px' }}>
              {/* Follower Count */}
              <div className="min-w-0" style={{ background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-lg" style={{ lineHeight: '28px', fontWeight: 500, color: subtitleColor }}>
                    {t('dashboard.charts.followerCount')}
                  </span>
                  {insightsCharts.followerData.data.length > 1 && (() => {
                    const diff = insightsCharts.followerData.data[insightsCharts.followerData.data.length - 1] - insightsCharts.followerData.data[0];
                    if (diff === 0) return null;
                    const isUp = diff > 0;
                    const TrendIcon = isUp ? ChevronsUp : ChevronsDown;
                    const colors = BADGE_COLORS[isUp ? 'up' : 'none'][isDark ? 'dark' : 'light'];
                    return (
                      <span
                        className="flex items-center"
                        style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, background: colors.bg, border: `1px solid ${colors.border}`, color: colors.color, borderRadius: '999px', padding: '4px 6px', gap: '2px' }}
                      >
                        <TrendIcon size={20} color={colors.chevron} />
                        {isUp ? `+${diff}` : `${diff}`}
                      </span>
                    );
                  })()}
                </div>
                {insightsCharts.followerData.categories.length > 0 ? (
                  <Chart
                    key={`follower-${isDark ? 'dark' : 'light'}`}
                    options={getEngagementChartOptions(insightsCharts.followerData.categories, insightsCharts.followerData.data, isDark ? '#00D492' : '#00BC7D', 'followerLineGradient')}
                    series={[{ name: t('dashboard.charts.followers'), data: insightsCharts.followerData.data }]}
                    type="area"
                    height={250}
                  />
                ) : (
                  <div className="flex items-center justify-center" style={{ padding: '48px 0' }}>
                    <span style={{ fontSize: '14px', color: subtitleColor }}>{t('dashboard.emptyStates.noFollowerData')}</span>
                  </div>
                )}
              </div>

              {/* Following Count */}
              <div className="min-w-0" style={{ background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-lg" style={{ lineHeight: '28px', fontWeight: 500, color: subtitleColor }}>
                    {t('dashboard.charts.followingCount')}
                  </span>
                  {insightsCharts.followingData.data.length > 1 && (() => {
                    const diff = insightsCharts.followingData.data[insightsCharts.followingData.data.length - 1] - insightsCharts.followingData.data[0];
                    if (diff === 0) return null;
                    const isUp = diff > 0;
                    const TrendIcon = isUp ? ChevronsUp : ChevronsDown;
                    const colors = BADGE_COLORS[isUp ? 'up' : 'none'][isDark ? 'dark' : 'light'];
                    return (
                      <span
                        className="flex items-center"
                        style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, background: colors.bg, border: `1px solid ${colors.border}`, color: colors.color, borderRadius: '999px', padding: '4px 6px', gap: '2px' }}
                      >
                        <TrendIcon size={20} color={colors.chevron} />
                        {isUp ? `+${diff}` : `${diff}`}
                      </span>
                    );
                  })()}
                </div>
                {insightsCharts.followingData.categories.length > 0 ? (
                  <Chart
                    key={`following-${isDark ? 'dark' : 'light'}`}
                    options={getEngagementChartOptions(insightsCharts.followingData.categories, insightsCharts.followingData.data, isDark ? '#C27AFF' : '#AD46FF', 'followingLineGradient')}
                    series={[{ name: t('dashboard.charts.following'), data: insightsCharts.followingData.data }]}
                    type="area"
                    height={250}
                  />
                ) : (
                  <div className="flex items-center justify-center" style={{ padding: '48px 0' }}>
                    <span style={{ fontSize: '14px', color: subtitleColor }}>{t('dashboard.emptyStates.noFollowingData')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ─── Engagement Over Time Section ─── */}
          <div className="flex flex-col" style={{ gap: '16px' }}>
            <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>
              {t('dashboard.charts.engagementOverTime', 'Engagement over time')}
            </span>
            <div className="min-w-0" style={{ background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Legend tags + action buttons row */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center" style={{ gap: '8px' }}>
                {/* Legend tags */}
                <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', flexWrap: 'wrap' }}>
                  {ENGAGEMENT_LEGEND.map((item) => (
                    <span
                      key={item.label}
                      style={{ fontSize: '12px', lineHeight: '18px', fontWeight: 500, background: item.bg, color: item.color, borderRadius: '8px', padding: '4px 8px' }}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
                {/* Action buttons */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`, borderRadius: '8px', overflow: 'hidden' }}>
                  {[
                    { icon: ZoomIn, action: () => { const cats = chartData.categories; const len = cats.length; if (len > 2) ApexCharts.exec('engagement-over-time', 'zoomX', Math.floor(len * 0.25), Math.floor(len * 0.75)); } },
                    { icon: ZoomOut, action: () => { ApexCharts.exec('engagement-over-time', 'updateOptions', { xaxis: { min: undefined, max: undefined } }, false, true); } },
                    { icon: ScanSearch, action: () => { const len = chartData.categories.length; if (len > 2) ApexCharts.exec('engagement-over-time', 'zoomX', 0, Math.floor(len * 0.5)); } },
                    { icon: RotateCcw, action: () => { ApexCharts.exec('engagement-over-time', 'updateOptions', { xaxis: { min: undefined, max: undefined } }, false, true); } },
                    { icon: EllipsisVertical, action: () => { ApexCharts.exec('engagement-over-time', 'dataURI').then((uri: { imgURI: string }) => { const a = document.createElement('a'); a.href = uri.imgURI; a.download = 'engagement-chart.png'; a.click(); }); } },
                  ].map((btn, i) => {
                    const IconComp = btn.icon;
                    return (
                      <button
                        key={i}
                        onClick={btn.action}
                        style={{
                          display: 'flex', justifyContent: 'center', alignItems: 'center',
                          width: '24px', height: '24px', padding: '4px',
                          background: mainBg,
                          borderTop: 'none', borderBottom: 'none', borderLeft: 'none',
                          borderRight: i < 4 ? `1px solid ${isDark ? '#314158' : '#E2E8F0'}` : 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <IconComp size={16} color={isDark ? '#E2E8F0' : '#314158'} />
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Chart */}
              {chartData.categories?.length > 0 && chartData.series?.some(s => s.data.length > 0) ? (
                <Chart
                  key={`engagement-${isDark ? 'dark' : 'light'}`}
                  options={mainChartOptions}
                  series={chartData.series}
                  type="area"
                  height={250}
                />
              ) : (
                <div className="flex items-center justify-center" style={{ padding: '48px 0' }}>
                  <span style={{ fontSize: '14px', color: subtitleColor }}>{t('dashboard.emptyStates.noChartData')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Modals ─── */}
      <InstagramReloginModal
        isOpen={isModalOpen}
        onClose={handleLater}
        onRelogin={handleRelogin}
        accountName={`@${selectedAccount?.username || 'unknown'}`}
        lockTitle={selectedAccount?.lock_title}
        lockDescription={selectedAccount?.lock_description}
        lockedByAdmin={selectedAccount?.locked_by_admin}
      />
      {isReauthOverlayOpen && (
        <AddAccountOverlay
          isOpen={isReauthOverlayOpen}
          onClose={() => { setIsReauthOverlayOpen(false); setReauthUsername(undefined); setReauthProfilePic(undefined); }}
          reauthUsername={reauthUsername}
          reauthProfilePic={reauthProfilePic}
          accountData={selectedAccount ? { _id: selectedAccount._id, username: selectedAccount.username, name: selectedAccount.name, profile_picture_base64: selectedAccount.imgSrc } : null}
          allAccounts={accounts.map(a => ({ _id: a._id, username: a.username, name: a.name, profile_picture_base64: a.imgSrc }))}
        />
      )}
    </div>
  );
};

export default Dashboard;
